import React, { useState, useEffect } from 'react';
import { useWalletMobile } from '../hooks/useWalletMobile';
import { useMobile } from '../hooks/useMobile';
import { Wallet, ExternalLink, QrCode, AlertCircle, CheckCircle } from 'lucide-react';

/**
 * Componente de conexão de carteira otimizado para mobile
 * Oferece UI touch-friendly e suporte a deep links
 */
const MobileWalletConnect = ({ onConnect, onError, showBalance = true }) => {
  const isMobile = useMobile();
  const {
    // Estado
    isConnected,
    isConnecting,
    currentAccount,
    balance,
    isPolygon,
    error,
    walletType,
    
    // Wallet info
    walletInfo,
    shouldShowMobileOptimizedUI,
    
    // Ações
    connectWallet,
    connectWalletConnect,
    disconnectWallet,
    switchToPolygon,
    
    // Utilitários
    needsPolygonSwitch,
    canUseWalletConnect
  } = useWalletMobile();

  const [showQRCode, setShowQRCode] = useState(false);
  const [connectionMethod, setConnectionMethod] = useState('auto'); // 'auto', 'metamask', 'walletconnect'

  // Notifica o componente pai sobre mudanças
  useEffect(() => {
    if (isConnected && currentAccount && onConnect) {
      onConnect({
        address: currentAccount,
        balance,
        isPolygon,
        walletType
      });
    }
  }, [isConnected, currentAccount, balance, isPolygon, walletType, onConnect]);

  // Notifica erros
  useEffect(() => {
    if (error && onError) {
      onError(error);
    }
  }, [error, onError]);

  // Conexão automática baseada no dispositivo
  const handleAutoConnect = async () => {
    const result = await connectWallet();
    
    if (result.redirect) {
      // Usuário foi redirecionado para app de carteira
      console.log('📱 Redirecionando para carteira...');
    }
  };

  // Conexão específica com MetaMask
  const handleMetaMaskConnect = async () => {
    setConnectionMethod('metamask');
    await connectWallet();
  };

  // Conexão com WalletConnect
  const handleWalletConnect = async () => {
    setConnectionMethod('walletconnect');
    await connectWalletConnect();
  };

  // Renderiza estado de conexão
  const renderConnectionState = () => {
    if (isConnecting) {
      return (
        <div className="mobile-wallet-connecting">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto mb-3"></div>
          <p className="text-gray-600 text-center mobile-text-lg">
            {connectionMethod === 'walletconnect' ? 'Abrindo WalletConnect...' : 'Conectando à carteira...'}
          </p>
        </div>
      );
    }

    if (isConnected) {
      return (
        <div className="mobile-wallet-connected">
          <div className="flex items-center justify-center mb-3">
            <CheckCircle className="w-6 h-6 text-green-500 mr-2" />
            <span className="text-green-600 font-medium mobile-text-lg">Conectado</span>
          </div>
          
          {/* Informações da conta */}
          <div className="bg-gray-50 rounded-lg p-3 mb-3">
            <p className="text-gray-800 text-sm font-mono text-center">
              {currentAccount?.slice(0, 8)}...{currentAccount?.slice(-6)}
            </p>
            
            {showBalance && balance && (
              <p className="text-green-600 text-center font-bold mobile-text-lg mt-2">
                {parseFloat(balance).toFixed(2)} RUA
              </p>
            )}
          </div>

          {/* Status da rede */}
          <div className={`rounded-lg p-3 mb-4 ${isPolygon ? 'bg-green-50 border border-green-200' : 'bg-yellow-50 border border-yellow-200'}`}>
            <div className="flex items-center justify-center">
              <div className={`w-2 h-2 rounded-full mr-2 ${isPolygon ? 'bg-green-500' : 'bg-yellow-500'}`}></div>
              <span className={`text-sm ${isPolygon ? 'text-green-700' : 'text-yellow-700'}`}>
                {isPolygon ? 'Polygon Network' : 'Rede não suportada'}
              </span>
            </div>
            
            {needsPolygonSwitch && (
              <button
                onClick={switchToPolygon}
                className="w-full mt-2 bg-yellow-500 text-white py-2 rounded-lg text-sm font-medium"
              >
                🚀 Mudar para Polygon
              </button>
            )}
          </div>

          {/* Botão desconectar */}
          <button
            onClick={disconnectWallet}
            className="w-full bg-gray-500 text-white py-3 rounded-lg font-medium mobile-btn"
          >
            🔓 Desconectar
          </button>
        </div>
      );
    }

    // Não conectado - mostrar opções de conexão
    return renderConnectionOptions();
  };

  // Opções de conexão para mobile
  const renderConnectionOptions = () => {
    // UI otimizada para mobile sem carteira injetada
    if (shouldShowMobileOptimizedUI) {
      return (
        <div className="mobile-wallet-options">
          <p className="text-gray-600 text-center mb-4 mobile-text-lg">
            📱 Conecte sua carteira
          </p>
          
          {/* MetaMask Mobile */}
          <button
            onClick={handleMetaMaskConnect}
            disabled={isConnecting}
            className="w-full bg-orange-500 text-white py-4 rounded-lg font-bold mb-3 mobile-btn-large flex items-center justify-center"
          >
            <Wallet className="w-5 h-5 mr-2" />
            Abrir MetaMask
          </button>

          {/* WalletConnect */}
          {canUseWalletConnect && (
            <button
              onClick={handleWalletConnect}
              disabled={isConnecting}
              className="w-full bg-blue-500 text-white py-4 rounded-lg font-bold mb-3 mobile-btn-large flex items-center justify-center"
            >
              <QrCode className="w-5 h-5 mr-2" />
              WalletConnect
            </button>
          )}

          {/* Fallback para instalação */}
          {!walletInfo.hasInjectedWeb3 && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mt-3">
              <p className="text-yellow-700 text-sm text-center">
                Não tem uma carteira?{' '}
                <a 
                  href="https://metamask.io/download.html" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="underline font-medium"
                >
                  Instale o MetaMask
                </a>
              </p>
            </div>
          )}
        </div>
      );
    }

    // UI para desktop ou mobile com carteira
    return (
      <div className="mobile-wallet-options">
        <button
          onClick={handleAutoConnect}
          disabled={isConnecting}
          className="w-full bg-gradient-to-r from-purple-500 to-pink-500 text-white py-4 rounded-lg font-bold mobile-btn-large flex items-center justify-center"
        >
          <Wallet className="w-5 h-5 mr-2" />
          {isConnecting ? 'Conectando...' : 'Conectar Carteira'}
        </button>

        {/* WalletConnect como alternativa */}
        {canUseWalletConnect && (
          <button
            onClick={handleWalletConnect}
            disabled={isConnecting}
            className="w-full border border-gray-300 text-gray-700 py-3 rounded-lg font-medium mt-2 mobile-btn flex items-center justify-center"
          >
            <QrCode className="w-4 h-4 mr-2" />
            Usar WalletConnect
          </button>
        )}
      </div>
    );
  };

  // Modal de QR Code para WalletConnect
  const renderQRCodeModal = () => {
    if (!showQRCode) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg max-w-sm w-full p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-bold">Scan QR Code</h3>
            <button 
              onClick={() => setShowQRCode(false)}
              className="text-gray-500 hover:text-gray-700"
            >
              ✕
            </button>
          </div>
          
          <div className="bg-gray-100 rounded-lg p-4 flex items-center justify-center">
            <div className="text-center">
              <QrCode className="w-24 h-24 text-gray-400 mx-auto mb-2" />
              <p className="text-gray-600 text-sm">
                Use seu app de carteira para scanear
              </p>
            </div>
          </div>
          
          <button
            onClick={() => setShowQRCode(false)}
            className="w-full bg-gray-500 text-white py-3 rounded-lg mt-4 font-medium"
          >
            Fechar
          </button>
        </div>
      </div>
    );
  };

  // Mensagem de erro
  const renderError = () => {
    if (!error) return null;

    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
        <div className="flex items-center">
          <AlertCircle className="w-4 h-4 text-red-500 mr-2" />
          <span className="text-red-700 text-sm">{error}</span>
        </div>
      </div>
    );
  };

  return (
    <div className="mobile-wallet-connect">
      {/* Header */}
      <div className="text-center mb-6">
        <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center mx-auto mb-3">
          <Wallet className="w-6 h-6 text-white" />
        </div>
        <h2 className="text-xl font-bold text-gray-800 mb-2">
          {isConnected ? 'Carteira Conectada' : 'Conectar Carteira'}
        </h2>
        <p className="text-gray-600 mobile-text-lg">
          {isConnected 
            ? 'Sua carteira está conectada à plataforma' 
            : 'Conecte sua carteira para começar'
          }
        </p>
      </div>

      {/* Conteúdo */}
      {renderError()}
      {renderConnectionState()}
      {renderQRCodeModal()}

      {/* Informações de suporte */}
      {!isConnected && (
        <div className="mt-6 text-center">
          <p className="text-gray-500 text-xs">
            Suporte: MetaMask, Trust Wallet, Coinbase Wallet e mais
          </p>
        </div>
      )}
    </div>
  );
};

// Componente simplificado para uso rápido
export const QuickWalletConnect = ({ onConnect }) => {
  const { isConnected, currentAccount, connectWallet } = useWalletMobile();

  if (isConnected) {
    return (
      <div className="quick-wallet-connected">
        <span className="text-green-600 text-sm">
          ✅ {currentAccount?.slice(0, 6)}...{currentAccount?.slice(-4)}
        </span>
      </div>
    );
  }

  return (
    <button
      onClick={connectWallet}
      className="quick-connect-btn bg-purple-500 text-white px-4 py-2 rounded-lg text-sm font-medium"
    >
      🔗 Conectar
    </button>
  );
};

export default MobileWalletConnect;
